<script setup lang="ts" generic="T">
import { nextTick, ref } from 'vue'
import draggable from 'vuedraggable'
import { announce } from '@/util/live_announcer'

const props = withDefaults(defineProps<{
  items: T[]
  itemKey: (item: T) => string
  itemLabel: (item: T) => string
  noun: string
  emptyFocusId: string
  reorderable?: boolean
}>(), {
  reorderable: false
})

const emit = defineEmits<{
  remove: [index: number]
  reorder: [items: T[]]
}>()

const list = ref<HTMLElement | null>(null)

type RowButton = 'data-remove-button' | 'data-move-up-button' | 'data-move-down-button'

function rowButtons(attribute: RowButton) {
  return list.value?.querySelectorAll<HTMLButtonElement>(`[${attribute}]`) ?? []
}

// Focus follows each removal or move, so a held Enter would otherwise keep clicking: down the
// list for remove, back and forth for move.
function ignoreKeyRepeat(event: KeyboardEvent) {
  if (event.repeat) event.preventDefault()
}

async function remove(index: number) {
  const label = props.itemLabel(props.items[index])
  emit('remove', index)
  announce(`Removed ${props.noun} ${label}`)
  await nextTick()
  const removeButtons = rowButtons('data-remove-button')
  const nextButton = removeButtons[Math.min(index, removeButtons.length - 1)]
  const focusTarget = nextButton ?? document.getElementById(props.emptyFocusId)
  focusTarget?.focus()
}

async function move(index: number, offset: -1 | 1) {
  const target = index + offset
  const reordered = [...props.items]
  const [moved] = reordered.splice(index, 1)
  reordered.splice(target, 0, moved)
  emit('reorder', reordered)
  announce(`Moved ${props.noun} ${props.itemLabel(moved)} to position ${target + 1} of ${reordered.length}`)
  await nextTick()
  // Keep focus on the moved row; at either end its button in that direction is disabled, so use the other one.
  const sameDirection = rowButtons(offset === -1 ? 'data-move-up-button' : 'data-move-down-button')[target]
  const otherDirection = rowButtons(offset === -1 ? 'data-move-down-button' : 'data-move-up-button')[target]
  ;(sameDirection?.disabled ? otherDirection : sameDirection)?.focus()
}
</script>

<template>
  <div v-if="items.length" ref="list">
    <draggable
      :model-value="items"
      :item-key="itemKey"
      :disabled="!reorderable"
      handle="[data-drag-handle]"
      tag="ul"
      class="mt-2 border border-gray-400 rounded-xs divide-y divide-gray-400"
      @update:model-value="(reordered: T[]) => emit('reorder', reordered)"
    >
      <template #item="{ element: item, index }">
        <li class="flex items-start gap-2 px-4 py-3">
          <div class="grow min-w-0" :class="{ 'cursor-move': reorderable }" :data-drag-handle="reorderable || undefined">
            <slot name="item" :item="item">{{ itemLabel(item) }}</slot>
          </div>
          <template v-if="reorderable">
            <button
              data-move-up-button
              type="button"
              class="shrink-0 inline-flex text-gray-900 disabled:text-gray-400 focus-visible:outline-primary-400"
              :aria-label="`Move ${noun} ${itemLabel(item)} up`"
              :disabled="index === 0"
              @keydown.enter="ignoreKeyRepeat"
              @click="move(index, -1)"
            >
              <span class="material-icons-round text-text-lg" aria-hidden="true">arrow_upward</span>
            </button>
            <button
              data-move-down-button
              type="button"
              class="shrink-0 inline-flex text-gray-900 disabled:text-gray-400 focus-visible:outline-primary-400"
              :aria-label="`Move ${noun} ${itemLabel(item)} down`"
              :disabled="index === items.length - 1"
              @keydown.enter="ignoreKeyRepeat"
              @click="move(index, 1)"
            >
              <span class="material-icons-round text-text-lg" aria-hidden="true">arrow_downward</span>
            </button>
          </template>
          <button
            data-remove-button
            type="button"
            class="shrink-0 inline-flex text-red-400 hover:text-red-300 focus-visible:outline-primary-400"
            :aria-label="`Remove ${noun} ${itemLabel(item)}`"
            @keydown.enter="ignoreKeyRepeat"
            @click="remove(index)"
          >
            <span class="material-icons-round text-text-lg" aria-hidden="true">close</span>
          </button>
        </li>
      </template>
    </draggable>
  </div>
</template>
